import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default:
          "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        // Semantic status variants, copied verbatim from VARIANT_CLASSES in
        // src/modules/app-studio/components/blocks/StatusBadge.tsx (PR-157): a `-subtle`
        // background with the matching `foreground` text and a soft border, never a
        // solid token as the fill. Measured: `foreground` text on its own `-subtle`
        // background reaches 17.97:1 (success) and 18.36:1 (warning) against the page
        // background, while the same text would read at 3.29:1 (warning) or 3.70:1
        // (success) directly on the page. The tokens carry their own light/dark value
        // via CSS variables, so no `dark:` variant is needed here.
        success: "bg-success-subtle text-success border-success/40",
        warning: "bg-warning-subtle text-warning border-warning/40",
        info: "bg-info-subtle text-info border-info/40",
        neutral: "bg-muted text-muted-foreground border-border",
        // The one status (`returned`, plus `refunded`/`disputed` -- see
        // TenantAdminOrders.tsx's UNMIGRATED_STATUS_COLORS) the six variants above cannot
        // represent without losing a distinct meaning already on screen. Same purple those
        // raw Tailwind classes paint, tokenized the same way as the four variants above it.
        returned: "bg-returned-subtle text-returned border-returned/40",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  )
}

export { Badge, badgeVariants }
